import { Component, OnInit, Input, Injectable, ViewChild, ElementRef } from '@angular/core';
import { IUProperty } from 'src/app/shared/interface/umbraco-property';
import { HttpClient } from '@angular/common/http';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subject, timer } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { RecaptchaComponent } from 'ng-recaptcha';
import { IButtonData } from '../../shared/components/bg-button/bg-button.component';
import { Router } from '@angular/router';
import { IRteStyleSettings } from '../../shared/interface/rte-style-settings';
import { applyRteSettings } from '../../shared/utils/rte-style-settings-utils';
import { ITitleStyleSettings } from '../../shared/interface/title-style-settings';
import { applyTitleSettings } from '../../shared/utils/title-style-settings-utils';

interface IDemoFormPanelData {
  title?: IUProperty<string>;
  capitalizeTitle: IUProperty<boolean>;
  description: IUProperty<string>;
  link: IUProperty<IButtonData>;
  linkColor: IUProperty<string>;
  mailType?: IUProperty<number>;
  cookiePolicyPageUrl: string;
  reCaptchaSecret: string;
  redirectLink?: IUProperty<IButtonData>;

  titleStyleSettings?: ITitleStyleSettings;
  rteStyleSettings?: IRteStyleSettings;
}

interface IDemoFormPanelResponseData {
  reCaptchaFailed: boolean;
}

@Component({
  selector: 'app-demo-form-panel',
  templateUrl: './demo-form-panel.component.html',
  styleUrls: ['./demo-form-panel.component.less']
})

@Injectable({
  providedIn: 'root'
})

export class DemoFormPanelComponent implements OnInit {
  @Input() data: IDemoFormPanelData;
  @ViewChild('titleEl', { static: false }) titleEl: ElementRef<HTMLElement>;
  @ViewChild('descriptionEl', { static: false }) descriptionEl: ElementRef<HTMLElement>;
  @ViewChild('captcha', { static: false }) captcha: RecaptchaComponent

  private alive$ = new Subject();

  formText = {
    name: this.translateService.instant("demoPanel.InputField.Name"),
    email: this.translateService.instant("demoPanel.InputField.Email"),
    phone: this.translateService.instant("demoPanel.InputField.Phone"),
    company: this.translateService.instant("demoPanel.InputField.Company"),
    submit: this.translateService.instant("demoPanel.SubmitButton.GetDemo")
  }
  errMsg: string = null;
  succMsg: string = null;
  captchaResponse: string = null;
  requestSuccess: boolean = false;
  submitButton: string = this.translateService.instant("demoPanel.SubmitButton.GetDemo");
  demoGroup: FormGroup;


  constructor (
    private http: HttpClient,
    private translateService: TranslateService,
    private router: Router,
  ) {}

  async ngOnInit() {
    this.demoGroup = new FormGroup({
      name: new FormControl("", Validators.required),
      email: new FormControl("", Validators.compose([Validators.required, Validators.email, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')])),
      phone: new FormControl("", Validators.required),
      company: new FormControl("", Validators.required),
      privacyPolicy: new FormControl(false, Validators.requiredTrue)
    });
  }

  ngAfterViewInit(): void {
    if (this.data && this.data.capitalizeTitle && this.data.capitalizeTitle.value && this.titleEl && this.titleEl.nativeElement) {
      this.titleEl.nativeElement.style.textTransform = 'uppercase';
    }
    applyRteSettings(this.data, this.descriptionEl);
    applyTitleSettings(this.data, this.titleEl);
  }

  reCaptchaValidated(response: string){
    this.captchaResponse = response;
  }

  sendRequest(event) {
    event.preventDefault();
    this.errMsg = "";

    if (!this.demoGroup.invalid && this.captchaResponse) {
      const data = {...this.demoGroup.value};
      data.mailType = this.data.mailType.value;
      data.reCaptchaToken = this.captchaResponse;
      this.http.post('ubaseline/api/demoFormPanel/sendRequest', data).subscribe(
        (data: IDemoFormPanelResponseData)  => { 
          if(data.reCaptchaFailed){
            this.errMsg = this.translateService.instant("reCaptcha.Error");
            this.resetCaptcha();
            return;
          }

          this.demoGroup.reset({
            name: '', 
            email: '', 
            phone: '', 
            company: '', 
            privacyPolicy: false, 
          }); 
          this.demoGroup.markAsUntouched();
          this.demoGroup.markAsPristine();
          this.successOnSend();

          if (this.data.redirectLink && this.data.redirectLink.value && this.data.redirectLink.value.url) {
            this.router.navigate([this.data.redirectLink.value.url]);
          }
        },
        error => console.log('An error has occured', error),
      )
    } else {
      if (this.demoGroup.controls.email.status === "INVALID") {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.Email");
      } else if (this.demoGroup.value.privacyPolicy) {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.MissingFields");
      } else if(!this.captchaResponse) {
        this.errMsg = this.translateService.instant("reCaptcha.CheckRecaptcha");
      } else {
        this.errMsg = this.translateService.instant("demoPanel.ErrorMessage.PrivacyPolicy");
      }
    }
  }

  resetCaptcha(){
    this.captcha.reset();
    this.captchaResponse = null;
  }

  successOnSend() {
    this.requestSuccess = true;
    this.succMsg = this.translateService.instant("demoPanel.SuccessMessage.OnSend");
    this.resetCaptcha();
    this.startTimer();
  }

  startTimer() {
    let countDown;
    let timeLeft: number = 5;
    const source = timer(1000, 1000);
    countDown = source.pipe(takeUntil(this.alive$)).subscribe(val => {
      timeLeft--;
      if (timeLeft === 0) {
        this.requestSuccess = false;
        countDown.unsubscribe();
      }
    });
  }

  ngOnDestroy() {
    this.alive$.next();
    this.alive$.complete();
  }
}
